/**
 * Summary / PDF location grouping (#999 Epic 4).
 *
 * Turns a flat repeatable-unit array into a render-ordered list where the first
 * unit of each location group carries a header marker. Reuses the shared
 * `groupUnitsByLocationThenStatus` model (location creation order → Untagged,
 * Existing then New within each group) so Summary and PDF stay aligned with the
 * step-list data model (NFR10, NFR14).
 *
 * Grouping is only active when the request has at least one location tag; with
 * no tags the original order is preserved and no headers are emitted (so
 * untouched requests render exactly as before).
 */

import {
  groupUnitsByLocationThenStatus,
  type LocationTagGroupingInput,
} from './groupUnitsByLocationThenStatus';
import { LOCATION_UNTAGGED_GROUP_LABEL } from './locationTagListGrouping';

export interface SummaryLocationGroupHeader {
  /** Tag name, or the shared "Untagged" label. */
  title: string;
  isUntagged: boolean;
  /** True for the first group rendered in the section (drops top margin). */
  isFirst: boolean;
  /** Stable testID fragment: `tag-<id>` or `untagged`. */
  testIDSuffix: string;
}

export interface SummaryLocationRenderUnit<T> {
  unit: T;
  /** Present on the first unit of each group; null otherwise. */
  header: SummaryLocationGroupHeader | null;
}

export interface BuildSummaryLocationRenderUnitsInput<T> {
  tags: LocationTagGroupingInput[];
  units: T[];
  getLocationTagId: (unit: T) => number | null | undefined;
  getStatus: (unit: T) => string | undefined;
  getDisplayOrder?: (unit: T) => number;
}

export function buildSummaryLocationRenderUnits<T>(
  input: BuildSummaryLocationRenderUnitsInput<T>,
): SummaryLocationRenderUnit<T>[] {
  const { tags, units } = input;

  if (!tags || tags.length === 0) {
    return units.map(unit => ({ unit, header: null }));
  }

  const groups = groupUnitsByLocationThenStatus({
    tags,
    units,
    getLocationTagId: input.getLocationTagId,
    getStatus: input.getStatus,
    getDisplayOrder: input.getDisplayOrder,
  });

  const result: SummaryLocationRenderUnit<T>[] = [];
  let isFirst = true;

  for (const group of groups) {
    const groupUnits = [...group.byStatus.existing, ...group.byStatus.new];
    if (groupUnits.length === 0) {
      continue;
    }

    const title = group.isUntagged
      ? LOCATION_UNTAGGED_GROUP_LABEL
      : group.locationTagName ?? LOCATION_UNTAGGED_GROUP_LABEL;
    const testIDSuffix = group.isUntagged
      ? 'untagged'
      : `tag-${group.locationTagId}`;

    groupUnits.forEach((unit, index) => {
      result.push({
        unit,
        header:
          index === 0
            ? { title, isUntagged: group.isUntagged, isFirst, testIDSuffix }
            : null,
      });
    });

    isFirst = false;
  }

  return result;
}
